import type { FC, ReactNode } from 'react'
import type { SchoolEvent } from '@/types'

import { Clock, MapPin, Users } from 'lucide-react'
import { cn } from '@/lib/utils'

interface EventListProps {
    events?: SchoolEvent[]
    headingLevel?: 'h3' | 'h4'
    showDescription?: boolean
    tone?: 'light' | 'dark'
}

/**
 * Upcoming events with a calendar-style date block. Every database text value
 * carries its data-mdlfy-cms tag so it can be edited straight from the preview.
 */
export const EventList: FC<EventListProps> = ({ events = [], headingLevel = 'h4', showDescription = false, tone = 'light' }): ReactNode => {
    const Heading = headingLevel
    const dark = tone === 'dark'

    if (events.length === 0) {
        return (
            <p className={cn('py-6 text-[0.95rem]', dark ? 'text-white/75' : 'text-muted-foreground')}>
                There are no upcoming events at the moment. New dates are added throughout the year, so please check back soon.
            </p>
        )
    }

    return (
        <ol className={cn('divide-y', dark ? 'divide-white/10' : 'divide-border')}>
            {events.map((event) => (
                <li key={event.id} className='flex gap-4 py-5 first:pt-0 last:pb-0'>
                    <div
                        aria-hidden='true'
                        className={cn(
                            'flex w-14 shrink-0 flex-col items-center justify-center self-start rounded-2xl py-2.5',
                            dark ? 'bg-white/10 text-white' : 'bg-navy text-white'
                        )}
                    >
                        <span className='text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-gold'>
                            {event.monthLabel}
                        </span>

                        <span className='font-heading text-2xl font-semibold leading-none'>
                            {event.dayLabel}
                        </span>
                    </div>

                    <div className='min-w-0'>
                        <Heading className={cn('font-semibold leading-snug', dark ? 'text-white' : 'text-navy')}>
                            <span data-mdlfy-cms={`events:${event.id}:title`}>{event.title}</span>
                        </Heading>

                        <p className={cn('mt-1.5 flex items-start gap-1.5 text-sm', dark ? 'text-white/75' : 'text-muted-foreground')}>
                            <Clock aria-hidden='true' className={cn('mt-0.5 size-4 shrink-0', dark ? 'text-gold' : 'text-gold-deep')} />

                            <span>
                                <time dateTime={event.startsAt}>{event.dateLabel}</time>
                                {event.timeLabel ? `, ${event.timeLabel}` : ', all day'}
                            </span>
                        </p>

                        {event.location && (
                            <p className={cn('mt-1 flex items-start gap-1.5 text-sm', dark ? 'text-white/75' : 'text-muted-foreground')}>
                                <MapPin aria-hidden='true' className={cn('mt-0.5 size-4 shrink-0', dark ? 'text-gold' : 'text-gold-deep')} />
                                <span data-mdlfy-cms={`events:${event.id}:location`}>{event.location}</span>
                            </p>
                        )}

                        {event.audience && (
                            <p className={cn('mt-1 flex items-start gap-1.5 text-sm', dark ? 'text-white/75' : 'text-muted-foreground')}>
                                <Users aria-hidden='true' className={cn('mt-0.5 size-4 shrink-0', dark ? 'text-gold' : 'text-gold-deep')} />
                                <span data-mdlfy-cms={`events:${event.id}:audience`}>{event.audience}</span>
                            </p>
                        )}

                        {showDescription && event.description && (
                            <p
                                data-mdlfy-cms={`events:${event.id}:description`}
                                className={cn('mt-2 text-[0.95rem] leading-relaxed', dark ? 'text-white/80' : 'text-foreground/80')}
                            >
                                {event.description}
                            </p>
                        )}
                    </div>
                </li>
            ))}
        </ol>
    )
}
